import React, { useState, useMemo, useCallback, useEffect } from 'react'
import * as yup from 'yup'
import Footer from '@app/components/Footer/Footer'
import Navbar from '@app/components/Navbar/Navbar'
import TextField from '@app/components/TextField/TextField'
import Button from '@app/components/Button/Button'
import {
  GetAddressDetailsResponse,
  GetAddressListResponse,
  useGetAddressDetailsQuery,
  useGetAddressListQuery,
} from '@app/store/api/customer'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import { useFormik } from 'formik'
import Tippy from '@tippyjs/react'
import Dropdown from '@app/components/Dropdown/Dropdown'
import SelectField from '@app/components/SelectField/SelectField'
import { QuizStep, updateQuiz } from '@app/store/slices/quiz'
import { useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'
import { Icon } from '@iconify/react'
import Box from '@app/components/Box/Box'
import useCampaignHref from '@app/hooks/useCampaignHref'
import S from './styles'
import QuizHeader from './components/QuizHeader/QuizHeader'
import AddressList from './components/AddressList/AddressList'
import { allowedCountries } from './constants'
import useQuizRedirect from './useQuizRedirect'
import RedirectPartialView from './RedirectPartialView'

const ShippingDetails = () => {
  const dispatch = useAppDispatch()
  const { messages } = useIntl()
  const quiz = useAppSelector(state => state.quiz)
  const { language } = useAppSelector(state => state.settings)
  const navigate = useNavigate()

  const { href: paymentHref } = useCampaignHref({ requestPath: 'quiz/payment' })
  const { isRedirecting } = useQuizRedirect({ requestedStep: QuizStep.ADDRESS })

  const [query, setQuery] = useState('')
  const [debounceQuery, setDebounceQuery] = useState('')
  const [queryId, setQueryId] = useState('')
  const { data } = useGetAddressListQuery({ query: debounceQuery, containerId: queryId }, { skip: !debounceQuery })

  const [addressId, setAddressId] = useState('')
  const { data: addressData } = useGetAddressDetailsQuery(addressId, { skip: !addressId })

  const [showForm, setShowForm] = useState(false)
  const isSearching = useMemo(() => query.length > 0, [query])

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      streetAddress: quiz.shippingAddressLine1,
      city: quiz.shippingAddressCity,
      postcode: quiz.shippingAddressPostcode,
      country: quiz.shippingAddressCountry,
    },
    validationSchema: yup.object({
      streetAddress: yup.string().required(messages['static.form.error_street_required'] as string),
      city: yup.string().required(messages['static.form.error_city_required'] as string),
      postcode: yup
        .string()
        .min(3, messages['static.form.error_postcode_invalid'] as string)
        .max(10, messages['static.form.error_postcode_invalid'] as string)
        .required(messages['static.form.error_postcode_required'] as string),
      country: yup.string().required(messages['static.form.error_country_required'] as string),
    }),
    onSubmit: values => {
      dispatch(
        updateQuiz({
          shippingAddressLine1: values.streetAddress,
          shippingAddressCity: values.city,
          shippingAddressPostcode: values.postcode,
          shippingAddressCountry: allowedCountries.find(c => c.code === values.country)?.[language] ?? '',
          shippingAddressCountryCode: values.country,
          step: QuizStep.PAYMENT,
          prevStep: QuizStep.ADDRESS,
        })
      )
    },
  })

  const debounceSearch = useMemo(() => {
    return debounce(q => {
      setDebounceQuery(q)
    }, 250)
  }, [])

  const update = (value: string) => {
    setQuery(value)
    debounceSearch(value)
    setQueryId('')
  }

  const enterManualAddress = async () => {
    setShowForm(true)
    setQuery('')
    await formik.setValues({ streetAddress: '', city: '', postcode: '', country: '' }, true)
    await formik.setTouched({ streetAddress: false, city: false, postcode: false, country: false })
  }

  const clickAddress = (address: GetAddressListResponse) => {
    if (address.type === 'Address') {
      setAddressId(address.id)
    } else {
      setQueryId(address.id)
    }
  }

  const selectAddress = useCallback(
    async (address: GetAddressDetailsResponse) => {
      setQuery('')
      setShowForm(false)

      await formik.setValues(
        {
          streetAddress: address.address,
          city: address.city,
          postcode: address.postal_code,
          country: address.country,
        },
        true
      )
    },
    [formik]
  )

  const clearQueryId = () => {
    setQueryId('')
  }

  useEffect(() => {
    if (addressData) {
      selectAddress(addressData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData])

  /**
   * Redirect to the next step if the step has changed in the store
   */
  useEffect(() => {
    if (quiz.step === QuizStep.PAYMENT) {
      navigate(paymentHref)
    }
  }, [navigate, quiz.step, paymentHref])

  if (isRedirecting) {
    return <RedirectPartialView />
  }

  return (
    <>
      <Navbar />
      <S.QuizMain>
        <QuizHeader
          title={<FormattedMessage id="quiz_address_page.content.base.title" />}
          intro={<FormattedMessage id="quiz_address_page.content.base.intro" />}
          nextStep={<FormattedMessage id="quiz_address_page.content.base.button_text" />}
          step={3}
          progress={53}
        />
        <S.Form onSubmit={formik.handleSubmit}>
          <S.Field>
            <Dropdown
              show={isSearching}
              edgeOffset={40}
              content={
                <>
                  {queryId && (
                    <Box pad={1}>
                      <S.TextButton onClick={clearQueryId}>
                        <Icon icon="bx:chevron-left" fontSize={20} />
                        <span>
                          <FormattedMessage id="static.actions.back" />
                        </span>
                      </S.TextButton>
                    </Box>
                  )}
                  <AddressList data={data ?? []} onClick={clickAddress} onNoAddress={enterManualAddress} />
                </>
              }>
              <TextField
                value={query}
                label={messages['quiz_address_page.content.questions.lookup_address.question'] as string}
                placeholder={messages['quiz_address_page.content.questions.lookup_address.placeholder'] as string}
                onChange={ev => update(ev.currentTarget.value)}
                onClear={() => update('')}
                autoComplete="off"
              />
            </Dropdown>
            {(formik.values.streetAddress || formik.values.city) && !showForm && (
              <S.AddressCard>
                <h5>
                  <FormattedMessage id="static.page.shipping.address_matched" />
                </h5>
                <address>
                  {formik.values.streetAddress}
                  <br />
                  {formik.values.city}
                  <br />
                  {formik.values.postcode}
                  <br />
                  {allowedCountries.find(c => c.code === formik.values.country)?.[language] ?? ''}
                </address>
              </S.AddressCard>
            )}
          </S.Field>
          {showForm && !isSearching && (
            <S.Field>
              <TextField
                name="streetAddress"
                label={
                  messages['quiz_address_page.content.questions.manual_enter_address.street_address_label'] as string
                }
                defaultValue={formik.values.streetAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.errors.streetAddress) && Boolean(formik.touched.streetAddress)
                    ? formik.errors.streetAddress
                    : undefined
                }
              />
              <TextField
                name="city"
                label={messages['quiz_address_page.content.questions.manual_enter_address.city_label'] as string}
                defaultValue={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.errors.city) && Boolean(formik.touched.city) ? formik.errors.city : undefined}
              />
              <TextField
                name="postcode"
                label={messages['quiz_address_page.content.questions.manual_enter_address.postal_code_label'] as string}
                defaultValue={formik.values.postcode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.errors.postcode) && Boolean(formik.touched.postcode)
                    ? formik.errors.postcode
                    : undefined
                }
              />
              <SelectField
                name="country"
                label={messages['quiz_address_page.content.questions.manual_enter_address.country_label'] as string}
                placeholder={messages['static.form.placeholder_select'] as string}
                options={allowedCountries
                  .map(c => ({ value: c.code, label: c[language] }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.errors.country) && Boolean(formik.touched.country) ? formik.errors.country : undefined
                }
              />
            </S.Field>
          )}
          <S.ActionBar>
            <Tippy
              content={<FormattedMessage id="static.form.hint_continue" />}
              placement="bottom"
              disabled={formik.isValid}>
              <div>
                <Button type="submit" disabled={!formik.isValid}>
                  <FormattedMessage id="quiz_address_page.content.base.button_text" />
                </Button>
              </div>
            </Tippy>
          </S.ActionBar>
        </S.Form>
      </S.QuizMain>
      <Footer />
    </>
  )
}

export default ShippingDetails
